import { useState } from 'react';

import { FileSignature, Phone, Search } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFinanceApplication, useFinanceApplications } from '@/hooks/useFinanceApplications';

import { FinanceApplicationDetails } from './components/FinanceApplicationDetails';

const FinanceApplicationsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useFinanceApplications({ search: search || undefined, limit: 100 });
  const { data: selected } = useFinanceApplication(selectedId ?? undefined);

  const applications = data?.data ?? [];

  const setSearch = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('search', value);
      else next.delete('search');
      return next;
    });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <FileSignature className="text-primary h-6 w-6" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Finance Applications</h1>
          <p className="text-muted-foreground text-sm">
            Pre-qualification details captured from callers during calls.
          </p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Search by name, email or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Applicant</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Employment</TableHead>
              <TableHead>Captured</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                  Loading…
                </TableCell>
              </TableRow>
            ) : applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                  No finance applications yet.
                </TableCell>
              </TableRow>
            ) : (
              applications.map((a) => (
                <TableRow key={a.id} className="cursor-pointer" onClick={() => setSelectedId(a.id)}>
                  <TableCell className="font-medium">
                    {a.applicant_name || a.call?.contact?.name || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{a.vehicle_name || '—'}</TableCell>
                  <TableCell>
                    {a.primary_telephone || a.call?.contact?.phone ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5" />
                        {a.primary_telephone || a.call?.contact?.phone}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {a.occupation || a.employment_basis || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(a.created_at as string).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selectedId} onOpenChange={(open) => !open && setSelectedId(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>{selected?.applicant_name || 'Finance application'}</span>
              <Badge variant="secondary">{selected?.status ?? 'captured'}</Badge>
            </DialogTitle>
          </DialogHeader>
          {selected ? (
            <>
              <FinanceApplicationDetails application={selected} />
              {selected.call_id ? (
                <button
                  type="button"
                  onClick={() => navigate(`/calls/${selected.call_id}`)}
                  className="text-primary mt-2 text-sm underline"
                >
                  View call
                </button>
              ) : null}
            </>
          ) : (
            <p className="text-muted-foreground py-6 text-center text-sm">Loading…</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceApplicationsPage;
